'use client'

import { useEffect, useCallback } from 'react'

declare global {
  interface Window {
    aplus_queue: any[]
    aplus?: {
      getMetaInfo?: (key: string) => any
      initParams?: (config: SDKConfig) => void
      use?: (pluginName: string, config: SDKConfig) => void
      qt_abtest?: {
        onABTestResultChange?: (res: ABTestResult[]) => void
        fetchABTestFromCache?: <T extends ABTestValueType>(
          params: ABTestFetchParams<T>
        ) => ABTestValueForType<T> | undefined
        fetchABTestFromServer?: <T extends ABTestValueType>(
          params: ABTestSDKAsyncFetchParams<T>
        ) => void
        fetchABTestFromCacheThenServer?: <T extends ABTestValueType>(
          params: ABTestSDKAsyncFetchParams<T>
        ) => void
      }
    }
    __QT_SYNC_INIT__?: boolean
  }
}

export type LogType = 'CLK' | 'EXP' | 'OTHER'
export type ABTestFetchStrategy =
  | 'fetchABTestFromCache'
  | 'fetchABTestFromServer'
  | 'fetchABTestFromCacheThenServer'
export type ABTestAsyncFetchStrategy =
  | 'fetchABTestFromServer'
  | 'fetchABTestFromCacheThenServer'
type ABTestValueTypeMap = {
  NUMBER: number
  STRING: string
  BOOLEAN: boolean
  JSON: Record<string, unknown> | null
}
export type ABTestValueType = keyof ABTestValueTypeMap
export type ABTestValueForType<T extends ABTestValueType> =
  ABTestValueTypeMap[T]
export type ABTestValue = ABTestValueForType<ABTestValueType> | undefined
const DEFAULT_ABTEST_TIMEOUT_MS = 3000

/**
 * QuickTracking 服务端 ABTest 接口返回的原始结果。
 * 实际返回值通常包含实验 ID、分组 ID，以及原始的 `value` 字段。
 */
export interface ABTestResult<TValue = unknown> {
  gid: string | number
  expid: string | number
  value?: TValue
  [key: string]: unknown
}

export interface ABTestAsyncResult extends ABTestResult<string> {
  value: string
}

export type ABTestAsyncValue<T extends ABTestValueType> =
  | ABTestAsyncResult
  | ABTestValueForType<T>
  | undefined

/**
 * QuickTracking ABTest 读取参数。
 *
 * 使用场景：
 * - `fetchABTestFromCache` 只读本地缓存，适合首屏快速兜底。
 * - `fetchABTestFromServer` 强制请求服务端，适合必须拿最新分流结果的场景。
 * - `fetchABTestFromCacheThenServer` 先返回缓存，再异步更新服务端结果，适合大多数页面。
 */
export interface ABTestFetchParams<
  T extends ABTestValueType = ABTestValueType,
> {
  /** 实验参数名，需要与阿里云实验平台中配置的参数名完全一致。 */
  param_name: string
  /** 参数值类型，必须和实验平台里定义的类型一致。 */
  value_type: T
  /** SDK 未加载、未命中实验或返回异常时使用的默认值。官方文档要求必传。 */
  default_value: ABTestValueForType<T> | undefined
}

/**
 * 需要异步请求服务端时使用的参数。
 */
export interface ABTestAsyncFetchParams<
  T extends ABTestValueType = ABTestValueType,
> extends ABTestFetchParams<T> {
  /** 等待服务端结果的超时时间，单位毫秒。SDK 或 Provider 超时后会回落到 `default_value`。 */
  timeout_milliseconds?: number
  /** 可选回调。服务端接口成功时会返回形如 `{ expid, gid, value }` 的对象。 */
  callback?: (result: ABTestAsyncValue<T>) => void
}

interface ABTestSDKAsyncFetchParams<
  T extends ABTestValueType = ABTestValueType,
> extends ABTestFetchParams<T> {
  timeout_milliseconds?: number
  callback: (result: ABTestAsyncValue<T>) => void
}

export interface SDKConfig {
  [key: string]: any
}

/**
 * QuickTracking 能力集合。
 * 所有业务代码都应该通过 `useQuickTracking()` 访问这些方法，而不是直接操作 `window.aplus`。
 */
export interface QuickTrackingAPI {
  /**
   * 上报自定义事件。
   *
   * @param eventName 事件名，建议使用稳定、可读的 snake_case。
   * @param properties 事件属性，会作为埋点附加字段一起上报。
   * @param logType 事件类型：`CLK` 点击、`EXP` 曝光、`OTHER` 其他。
   *
   * @example
   * trackEvent('buy_gem_click', { source: 'paywall' }, 'CLK')
   */
  trackEvent: (
    eventName: string,
    properties: Record<string, any> | undefined,
    logType: LogType
  ) => void
  /**
   * 上报页面浏览。
   *
   * @param pageInfo 页面附加信息，例如 `page_name`、`page_url`、来源页等。
   *
   * @example
   * sendPageView({ page_name: 'gem_store', page_url: window.location.href })
   */
  sendPageView: (pageInfo?: Record<string, any>) => void
  /**
   * 上报 Deeplink / App Link 打开事件。
   *
   * @param linkInfo 链路信息，例如 deeplink 地址、来源渠道、跳转目标等。
   */
  recordAppLink: (linkInfo?: Record<string, any>) => void
  /**
   * 设置单个 Meta 信息。
   *
   * @param key Meta 键名，例如 `_user_id`、`appKey`。
   * @param value Meta 值。
   */
  setMetaInfo: (key: string, value: any) => void
  /**
   * 设置当前登录用户 ID。
   * 建议在登录成功后、拉取用户分群或 ABTest 之前调用。
   *
   * @param userId 当前用户唯一标识。
   */
  setUserId: (userId: string) => void
  /**
   * 批量追加 Meta 信息。
   *
   * @param config 要追加的键值对。
   */
  appendMetaInfo: (config: SDKConfig) => void
  /**
   * 通用 ABTest 读取入口。
   *
   * 注意：
   * - `fetchABTestFromCache` 返回同步值。
   * - `fetchABTestFromServer` / `fetchABTestFromCacheThenServer` 返回 Promise。
   *
   * @param strategy 读取策略。
   * @param params 对应策略的入参。
   */
  fetchABTestValue: <T extends ABTestValueType>(
    strategy: ABTestFetchStrategy,
    params: ABTestFetchParams<T> | ABTestAsyncFetchParams<T>
  ) => ABTestValueForType<T> | undefined | Promise<ABTestAsyncValue<T>>
  /**
   * 只读取本地缓存中的实验值，不会触发网络请求。
   *
   * @param params `param_name` 为实验参数名，`value_type` 为值类型，`default_value` 为兜底值。
   * @returns 缓存中的实验值；未命中时返回 `default_value`。
   *
   * @example
   * const variant = fetchABTestFromCache({
   *   param_name: 'paywall_variant',
   *   value_type: 'STRING',
   *   default_value: 'control',
   * })
   */
  fetchABTestFromCache: <T extends ABTestValueType>(
    params: ABTestFetchParams<T>
  ) => ABTestValueForType<T> | undefined
  /**
   * 强制从服务端拉取实验值。
   *
   * @param params
   * `param_name`: 实验参数名。
   * `value_type`: 返回值类型，必须与实验平台一致。
   * `default_value`: 兜底值。
   * `timeout_milliseconds`: 可选超时，避免页面长期等待。
   * `callback`: 可选回调，兼容官方 SDK 的回调式调用。
   * @returns Promise 形式的实验结果；成功时通常为 `{ expid, gid, value }`，失败时回落到 `default_value`。
   *
   * @example
   * const variant = await fetchABTestFromServer({
   *   param_name: 'paywall_variant',
   *   value_type: 'STRING',
   *   default_value: 'control',
   *   timeout_milliseconds: 3000,
   * })
   */
  fetchABTestFromServer: <T extends ABTestValueType>(
    params: ABTestAsyncFetchParams<T>
  ) => Promise<ABTestAsyncValue<T>>
  /**
   * 先返回缓存值，再由 SDK 异步拉取服务端最新结果。
   * 这是页面实验分流的推荐用法。
   *
   * @param params 与 `fetchABTestFromServer` 相同。
   * @returns Promise 形式的实验结果；成功时通常为 `{ expid, gid, value }`，失败时回落到 `default_value`。
   *
   * @example
   * const variant = await fetchABTestFromCacheThenServer({
   *   param_name: 'paywall_variant',
   *   value_type: 'STRING',
   *   default_value: 'control',
   *   timeout_milliseconds: 3000,
   * })
   */
  fetchABTestFromCacheThenServer: <T extends ABTestValueType>(
    params: ABTestAsyncFetchParams<T>
  ) => Promise<ABTestAsyncValue<T>>
  /**
   * 订阅实验结果变化。
   * 注意：底层 SDK 仅暴露单个 `onABTestResultChange` 入口，
   * 同一时刻只能有一个 listener。后续订阅会覆盖先前订阅。
   *
   * @param listener 当 SDK 监听到实验结果更新时触发。
   * @returns 取消订阅函数。
   *
   * @example
   * const unsubscribe = subscribeABTestResultChange((results) => {
   *   console.log(results)
   * })
   */
  subscribeABTestResultChange: (
    listener: (res: ABTestResult[]) => void
  ) => () => void
}

/**
 * QuickTracking Hook。
 *
 * 使用方式：
 * ```ts
 * const { trackEvent, fetchABTestFromCacheThenServer, setUserId } =
 *   useQuickTracking()
 * ```
 */
export function useQuickTracking(): QuickTrackingAPI {
  const trackEvent = useCallback(
    (
      eventName: string,
      properties: Record<string, any> | undefined,
      logType: LogType
    ) => {
      if (typeof window === 'undefined' || !window.aplus_queue) {
        console.warn('QuickTracking SDK not loaded yet')
        return
      }

      window.aplus_queue.push({
        action: 'aplus.record',
        arguments: [eventName, logType, properties || {}],
      })
    },
    []
  )

  const sendPageView = useCallback((pageInfo?: Record<string, any>) => {
    if (typeof window === 'undefined' || !window.aplus_queue) {
      console.warn('QuickTracking SDK not loaded yet')
      return
    }

    window.aplus_queue.push({
      action: 'aplus.sendPV',
      arguments: pageInfo ? [pageInfo] : [],
    })
  }, [])

  const recordAppLink = useCallback((linkInfo?: Record<string, any>) => {
    if (typeof window === 'undefined' || !window.aplus_queue) {
      console.warn('QuickTracking SDK not loaded yet')
      return
    }

    window.aplus_queue.push({
      action: 'aplus.recordAppLink',
      arguments: linkInfo ? [linkInfo] : [],
    })
  }, [])

  const setMetaInfo = useCallback((key: string, value: any) => {
    if (typeof window === 'undefined' || !window.aplus_queue) {
      console.warn('QuickTracking SDK not loaded yet')
      return
    }

    window.aplus_queue.push({
      action: 'aplus.setMetaInfo',
      arguments: [key, value],
    })
  }, [])

  const setUserId = useCallback((userId: string) => {
    if (typeof window === 'undefined' || !window.aplus_queue) {
      console.warn('QuickTracking SDK not loaded yet')
      return
    }

    window.aplus_queue.push({
      action: 'aplus.setMetaInfo',
      arguments: ['_user_id', userId],
    })
  }, [])

  const appendMetaInfo = useCallback((config: SDKConfig) => {
    if (typeof window === 'undefined' || !window.aplus_queue) {
      console.warn('QuickTracking SDK not loaded yet')
      return
    }

    window.aplus_queue.push({
      action: 'aplus.appendMetaInfo',
      arguments: [config],
    })
  }, [])

  const fetchABTestFromCache = useCallback(function <T extends ABTestValueType>(
    params: ABTestFetchParams<T>
  ): ABTestValueForType<T> | undefined {
    if (typeof window === 'undefined' || !window.aplus?.qt_abtest) {
      console.warn('QuickTracking ABTest SDK not loaded yet')
      return params.default_value
    }

    const fetcher = window.aplus.qt_abtest.fetchABTestFromCache
    if (typeof fetcher !== 'function') {
      console.warn(
        'QuickTracking ABTest method fetchABTestFromCache not available'
      )
      return params.default_value
    }

    try {
      return fetcher(params) ?? params.default_value
    } catch (error) {
      console.error('QuickTracking fetchABTestFromCache failed:', error)
      return params.default_value
    }
  }, [])

  const fetchABTestAsyncValue = useCallback(function <
    T extends ABTestValueType,
  >(
    strategy: ABTestAsyncFetchStrategy,
    params: ABTestAsyncFetchParams<T>
  ): Promise<ABTestAsyncValue<T>> {
    if (typeof window === 'undefined' || !window.aplus?.qt_abtest) {
      console.warn('QuickTracking ABTest SDK not loaded yet')
      params.callback?.(params.default_value)
      return Promise.resolve(params.default_value)
    }

    const fetcher =
      strategy === 'fetchABTestFromServer'
        ? window.aplus.qt_abtest.fetchABTestFromServer
        : window.aplus.qt_abtest.fetchABTestFromCacheThenServer

    if (typeof fetcher !== 'function') {
      console.warn(`QuickTracking ABTest method ${strategy} not available`)
      params.callback?.(params.default_value)
      return Promise.resolve(params.default_value)
    }

    return new Promise((resolve) => {
      const timeoutMilliseconds =
        typeof params.timeout_milliseconds === 'number' &&
        Number.isFinite(params.timeout_milliseconds) &&
        params.timeout_milliseconds > 0
          ? params.timeout_milliseconds
          : DEFAULT_ABTEST_TIMEOUT_MS

      let settled = false

      const handleResult = (result: ABTestAsyncValue<T>) => {
        if (settled) {
          return
        }

        settled = true
        if (timeoutId) {
          clearTimeout(timeoutId)
        }

        const finalResult = result ?? params.default_value
        try {
          params.callback?.(finalResult)
        } catch (error) {
          console.error(`QuickTracking ${strategy} callback failed:`, error)
        }
        resolve(finalResult)
      }

      const timeoutId = setTimeout(() => {
        console.warn(
          `QuickTracking ${strategy} timed out after ${timeoutMilliseconds}ms`
        )
        handleResult(params.default_value)
      }, timeoutMilliseconds)

      try {
        fetcher({
          ...params,
          timeout_milliseconds: timeoutMilliseconds,
          callback: handleResult,
        })
      } catch (error) {
        console.error(`QuickTracking ${strategy} failed:`, error)
        handleResult(params.default_value)
      }
    })
  }, [])

  const fetchABTestValue = useCallback(
    function <T extends ABTestValueType>(
      strategy: ABTestFetchStrategy,
      params: ABTestFetchParams<T> | ABTestAsyncFetchParams<T>
    ) {
      if (strategy === 'fetchABTestFromCache') {
        return fetchABTestFromCache(params as ABTestFetchParams<T>)
      }

      return fetchABTestAsyncValue(
        strategy,
        params as ABTestAsyncFetchParams<T>
      )
    },
    [fetchABTestAsyncValue, fetchABTestFromCache]
  )

  const fetchABTestFromServer = useCallback(
    function <T extends ABTestValueType>(params: ABTestAsyncFetchParams<T>) {
      return fetchABTestAsyncValue('fetchABTestFromServer', params)
    },
    [fetchABTestAsyncValue]
  )

  const fetchABTestFromCacheThenServer = useCallback(
    function <T extends ABTestValueType>(params: ABTestAsyncFetchParams<T>) {
      return fetchABTestAsyncValue('fetchABTestFromCacheThenServer', params)
    },
    [fetchABTestAsyncValue]
  )

  const subscribeABTestResultChange = useCallback(
    (listener: (res: ABTestResult[]) => void) => {
      if (typeof window === 'undefined' || !window.aplus?.qt_abtest) {
        console.warn('QuickTracking ABTest SDK not loaded yet')
        return () => undefined
      }

      if (
        window.aplus.qt_abtest.onABTestResultChange &&
        window.aplus.qt_abtest.onABTestResultChange !== listener
      ) {
        console.warn(
          'QuickTracking ABTest result listener already exists and will be overwritten'
        )
      }

      window.aplus.qt_abtest.onABTestResultChange = listener

      return () => {
        if (window.aplus?.qt_abtest?.onABTestResultChange === listener) {
          window.aplus.qt_abtest.onABTestResultChange = undefined
        }
      }
    },
    []
  )

  return {
    trackEvent,
    sendPageView,
    recordAppLink,
    setMetaInfo,
    setUserId,
    appendMetaInfo,
    fetchABTestValue,
    fetchABTestFromCache,
    fetchABTestFromServer,
    fetchABTestFromCacheThenServer,
    subscribeABTestResultChange,
  }
}

export function QuickTrackingProvider({
  children,
}: {
  children: React.ReactNode
}) {
  useEffect(() => {
    if (typeof window !== 'undefined' && window.__QT_SYNC_INIT__) {
      return
    }

    const appKey = process.env.NEXT_PUBLIC_QUICK_TRACKING_APP_KEY
    const trackDomain = process.env.NEXT_PUBLIC_QUICK_TRACKING_TRACK_DOMAIN

    if (typeof window !== 'undefined' && window.aplus_queue && appKey) {
      window.aplus_queue.push({
        action: 'aplus.setMetaInfo',
        arguments: ['appKey', appKey],
      })
    }

    if (typeof window !== 'undefined' && window.aplus_queue && trackDomain) {
      window.aplus_queue.push({
        action: 'aplus.setMetaInfo',
        arguments: ['trackDomain', trackDomain],
      })
    }
  }, [])

  return <>{children}</>
}
